<template>
  <v-navigation-drawer
    v-model="$store.menuStore.state.interactionsSidebarStatus"
    absolute
    temporary
    right
    app
    :width="$vuetify.breakpoint.mobile ? '100%' : '600'"
  >
  <div class="interactions-sidebar">
    <div class="interactions-header px-4 py-3">
      <div class="d-flex align-center min-width-0">
        <v-btn icon small class="mr-2" @click="backToProject">
          <v-icon color="#2D4B95">mdi-arrow-left</v-icon>
        </v-btn>
        <div class="min-width-0">
          <p class="mb-0 interactions-title">Interacciones</p>
          <p class="mb-0 caption text--secondary text-truncate">
            {{ totalLabel }}
          </p>
        </div>
      </div>
      <v-btn icon small @click="closeSidebar">
        <v-icon color="#2D4B95">mdi-close-box</v-icon>
      </v-btn>
    </div>

    <v-divider></v-divider>

    <div v-if="canFilterUsers" class="user-filter px-4 pt-3 pb-3">
      <v-select
        v-model="selectedUserId"
        :items="users"
        item-text="name"
        item-value="id"
        label="Filtrar por usuario"
        placeholder="Mis interacciones"
        prepend-inner-icon="mdi-account-filter-outline"
        clearable
        outlined
        dense
        hide-details
        :loading="$store.usersStore.state.loadingUserListForItems"
        @focus="ensureUsers"
      ></v-select>
      <div class="user-filter-hint mt-2">
        <v-icon size="14" color="#5c6b8a" class="mr-1">
          {{ selectedUserId ? 'mdi-account-eye-outline' : 'mdi-account-circle-outline' }}
        </v-icon>
        <span>
          Viendo interacciones de
          <strong>{{ selectedUserId ? selectedUserName : 'ti (usuario actual)' }}</strong>
        </span>
      </div>
    </div>

    <v-divider v-if="canFilterUsers"></v-divider>

    <div class="interactions-content">
      <div v-if="loading" class="px-4 py-3">
        <v-skeleton-loader
          v-for="index in 4"
          :key="index"
          type="list-item-three-line"
          class="mb-2 skeleton-item"
        ></v-skeleton-loader>
      </div>

      <div v-else-if="error" class="empty-state px-5">
        <v-icon color="#F4516C" size="40">mdi-alert-circle-outline</v-icon>
        <p class="mb-2 mt-3 font-weight-bold text-primary">No se pudieron cargar las interacciones</p>
        <v-btn small outlined color="#2D4B95" @click="loadInteractions">
          <v-icon small left>mdi-refresh</v-icon>
          Reintentar
        </v-btn>
      </div>

      <div v-else-if="interactions.length === 0" class="empty-state px-5">
        <v-icon color="#c5d3f0" size="44">mdi-message-text-outline</v-icon>
        <p class="mb-0 mt-3 body-2 text--secondary">No hay interacciones para mostrar</p>
      </div>

      <div v-else class="interaction-list px-3 py-2">
        <v-card
          v-for="interaction in interactions"
          :key="`${interaction.interaction_type}-${interaction.id}`"
          class="interaction-item clickable"
          :class="`interaction-item--${interaction.type}`"
          outlined
          @click="openInteraction(interaction)"
        >
          <div class="interaction-item-inner">
            <div class="interaction-item-row">
              <div class="interaction-meta min-width-0">
                <span
                  class="interaction-type"
                  :style="{ color: typeColor(interaction.type), background: typeColor(interaction.type) + '1a' }"
                >
                  {{ interactionTypeLabel(interaction) }}
                </span>
                <span class="interaction-number">Nº{{ interaction.number || '-' }}</span>
              </div>
              <span class="interaction-date flex-shrink-0">{{ interaction.created_at | toDateTime }}</span>
            </div>

            <div class="interaction-body">
              <span class="interaction-name">{{ interaction.name || 'Elemento sin nombre' }}</span>
              <span class="interaction-sep">—</span>
              <span class="interaction-message" v-html="interaction.message || 'Sin contenido'"></span>
            </div>
          </div>
        </v-card>
      </div>
    </div>

    <div v-if="lastPage > 1" class="interactions-pagination px-4 py-3">
      <v-pagination
        v-model="currentPage"
        color="#2D4B95"
        :length="lastPage"
        :total-visible="$vuetify.breakpoint.mobile ? 3 : 5"
        circle
        @input="changePage"
      ></v-pagination>
    </div>
  </div>
  </v-navigation-drawer>
</template>

<script>
import {getTareaByID} from '@/helpers/api/tasks'
import {getRdiByID} from '@/helpers/api/rdi'
import {getMeeting} from '@/helpers/api/meetings'

const TYPE_COLORS = {
  task: '#2D4B95',
  rdi: '#E8912D',
  additional: '#7E57C2',
  meeting: '#2E9E6C'
}

export default {
  name: 'InteractionsSidebar',
  computed: {
    selectedProject() {
      return this.$store.projectsStore.state.selectedProject
    },
    projectId() {
      return this.selectedProject ? this.selectedProject.id : null
    },
    interactionsState() {
      return this.$store.interactionsStore.state
    },
    interactions() {
      return this.interactionsState.interactions
    },
    loading() {
      return this.interactionsState.loading
    },
    error() {
      return this.interactionsState.error
    },
    currentPage: {
      get() {
        return this.interactionsState.currentPage
      },
      set(page) {
        this.$store.interactionsStore.commit('setCurrentPage', page)
      }
    },
    lastPage() {
      return this.interactionsState.lastPage
    },
    total() {
      return this.interactionsState.total
    },
    selectedUserId: {
      get() {
        return this.interactionsState.selectedUserId
      },
      set(userId) {
        this.changeUser(userId)
      }
    },
    selectedUserName() {
      const user = this.users.find(u => u.id === this.selectedUserId)
      return user ? user.name : ''
    },
    canFilterUsers() {
      return this.checkPermissions(['view_users_interactions'])
    },
    users() {
      return this.$store.usersStore.state.usersListForItems.users || []
    },
    totalLabel() {
      if (this.loading) return 'Cargando interacciones...'
      return `${this.total} interacción${this.total === 1 ? '' : 'es'}`
    }
  },
  watch: {
    projectId(newProjectId, oldProjectId) {
      if (newProjectId === oldProjectId) return
      this.loadInteractions()
      if (this.canFilterUsers) this.ensureUsers()
    }
  },
  mounted() {
    this.loadInteractions()
    if (this.canFilterUsers) this.ensureUsers()
  },
  methods: {
    loadInteractions() {
      if (!this.projectId) return
      this.$store.interactionsStore.dispatch('getInteractions', {
        project_id: this.projectId,
        page: this.currentPage,
        per_page: this.interactionsState.perPage,
        user_id: this.canFilterUsers ? this.selectedUserId : null
      })
    },
    changePage(page) {
      this.$store.interactionsStore.dispatch('getInteractions', {
        project_id: this.projectId,
        page,
        per_page: this.interactionsState.perPage,
        user_id: this.canFilterUsers ? this.selectedUserId : null
      })
    },
    changeUser(userId) {
      this.$store.interactionsStore.commit('setSelectedUserId', userId || null)
      this.$store.interactionsStore.commit('setCurrentPage', 1)
      this.loadInteractions()
    },
    ensureUsers() {
      const usersState = this.$store.usersStore.state.usersListForItems
      if (!this.projectId || this.$store.usersStore.state.loadingUserListForItems) return
      if (usersState.project_id !== this.projectId || usersState.users.length === 0) {
        this.$store.usersStore.dispatch('getUsersListForItems', { project_id: this.projectId })
      }
    },
    interactionTypeLabel(interaction) {
      const labels = {
        task: 'Tarea',
        rdi: 'RDI',
        additional: 'Adicional',
        meeting: 'Reunión/Minuta'
      }
      return labels[interaction.type] || interaction.type || 'Interacción'
    },
    typeColor(type) {
      return TYPE_COLORS[type] || '#5c6b8a'
    },
    closeSidebar() {
      this.$store.menuStore.commit('setInteractionsSidebarStatus', false)
    },
    backToProject() {
      this.closeSidebar()
      this.$store.menuStore.commit('setSidebarStatus', true)
    },
    async openInteraction(interaction) {
      this.closeSidebar()
      const itemId = Number(interaction.item_id)
      if (!itemId) return

      if (interaction.type === 'task') {
        this.$store.consultasStore.commit('setCurrentTarea', {loading: true})
        const response = await getTareaByID({task_id: itemId})
        if (response.code === 200) {
          const task = response.task
          task.prev_seen_by_me = false
          this.$store.consultasStore.commit('setCurrentTarea', task)
        }
      } else if (interaction.type === 'rdi') {
        this.$store.rdiStore.commit('setCurrentRDI', {loading: true})
        const response = await getRdiByID({rdi_id: itemId})
        if (response.code === 200) {
          const rdi = response.rdi
          rdi.prev_seen_by_me = false
          this.$store.rdiStore.commit('setCurrentRDI', rdi)
        }
      } else if (interaction.type === 'additional') {
        this.$store.adicionalesStore.commit('setCurrentAdicional', {loading: true})
        this.$store.adicionalesStore.dispatch('getAdditionalByID', {
          additional_id: itemId,
          seen_by_me: false
        })
      } else if (interaction.type === 'meeting') {
        this.$store.meetingStore.commit('setCurrentMeeting', {loading: true})
        const response = await getMeeting({meeting_id: itemId})
        if (response.code === 200) this.$store.meetingStore.commit('setCurrentMeeting', response.meeting)
        else if (response.code === 403) this.$store.meetingStore.commit('setCurrentMeeting', null)
      }
    }
  }
}
</script>

<style scoped>
.interactions-sidebar {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: #f7f8fc;
}
.interactions-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #eef1fb;
}
.interactions-title {
  color: #2D4B95;
  font-size: 16px;
  font-weight: 700;
}

/* User filter */
.user-filter {
  background: #ffffff;
}
.user-filter-hint {
  display: flex;
  align-items: center;
  font-size: 11.5px;
  color: #5c6b8a;
  background: #eef1fb;
  border-radius: 6px;
  padding: 5px 8px;
}
.user-filter-hint strong {
  color: #2D4B95;
  font-weight: 700;
  margin-left: 3px;
}

.interactions-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
.interaction-list {
  min-width: 0;
}
.skeleton-item {
  border-radius: 8px;
}

/* Interaction card */
.interaction-item {
  border-color: #e4e8f2 !important;
  border-radius: 8px !important;
  margin-bottom: 8px;
  max-height: 100px;
  overflow: hidden;
  background: #fff;
  border-left: 3px solid transparent !important;
  position: relative;
  transition: box-shadow .15s ease, border-color .15s ease, transform .1s ease;
}
.interaction-item::after {
  content: '';
  position: absolute;
  left: 3px;
  right: 0;
  bottom: 0;
  height: 18px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0), #fff 90%);
  pointer-events: none;
}
.interaction-item.clickable:hover::after {
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0), #fbfcff 90%);
}
.interaction-item--task { border-left-color: #2D4B95 !important; }
.interaction-item--rdi { border-left-color: #E8912D !important; }
.interaction-item--additional { border-left-color: #7E57C2 !important; }
.interaction-item--meeting { border-left-color: #2E9E6C !important; }

.interaction-item.clickable:hover {
  box-shadow: 0 2px 8px rgba(45, 75, 149, 0.14);
  border-color: #c5d3f0 !important;
}
.interaction-item.clickable:active {
  transform: scale(0.995);
}

.interaction-item-inner {
  height: 100px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: hidden;
}

.interaction-item-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.interaction-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
}
.interaction-type {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .3px;
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
}
.interaction-number {
  font-size: 11px;
  font-weight: 600;
  color: #6b7592;
  white-space: nowrap;
}
.interaction-date {
  font-size: 10.5px;
  color: #8a94a8;
  white-space: nowrap;
}

.interaction-body {
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
  font-size: 12.5px;
}
.interaction-name {
  font-weight: 700;
  color: #232b3d;
}
.interaction-sep {
  color: #b7c0d6;
  margin: 0 4px;
}
.interaction-message {
  color: #6b7592;
}
.interaction-message >>> p,
.interaction-message >>> div {
  display: inline;
  margin: 0;
  padding: 0;
}
.interaction-message >>> br {
  content: ' ';
  display: inline;
}
.interaction-message >>> br::after {
  content: ' ';
}
.interaction-message >>> ul,
.interaction-message >>> ol {
  display: inline;
  margin: 0;
  padding: 0;
  list-style: none;
}
.interaction-message >>> li {
  display: inline;
}
.interaction-message >>> li::after {
  content: ' · ';
}
.interaction-message >>> li:last-child::after {
  content: '';
}
.interaction-message >>> strong,
.interaction-message >>> b {
  font-weight: 600;
  color: #4a5568;
}
.interaction-message >>> a {
  color: inherit;
  text-decoration: underline;
}
.interaction-message >>> img {
  display: none;
}

/* Pagination */
.interactions-pagination {
  border-top: 1px solid #e4e8f2;
  background: #fff;
  display: flex;
  justify-content: center;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 220px;
  text-align: center;
}
.min-width-0 {
  min-width: 0;
}
.flex-shrink-0 {
  flex-shrink: 0;
}
</style>